/**
 * Everything that happened to a lead, in one column, newest first — messages, handoffs, notes,
 * stage moves, score recomputes, escalations. Interleaved by time because that is the only order
 * in which a human can reconstruct what went on.
 *
 * Author: Danish Khan
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { TimelineEntry } from '../lib/types.ts';
import { formatAgo, formatDateTime } from '../lib/format.ts';
import { EmptyState } from './EmptyState.tsx';

const KIND_TONE: Record<TimelineEntry['kind'], string> = {
  message: 'bg-neutral-soft text-neutral-soft-fg',
  handoff: 'bg-brand-soft text-brand-soft-fg',
  note: 'bg-info-soft text-info-soft-fg',
  task: 'bg-info-soft text-info-soft-fg',
  promise: 'bg-warn-soft text-warn-soft-fg',
  escalation: 'bg-danger-soft text-danger-soft-fg',
  stage: 'bg-ok-soft text-ok-soft-fg',
  score: 'bg-neutral-soft text-neutral-soft-fg',
};

export function Timeline({ entries }: { entries: TimelineEntry[] }): ReactNode {
  if (!entries.length) {
    return <EmptyState title="Nothing has happened yet" body="Messages, notes and handoffs will appear here." />;
  }
  return (
    <ol className="relative flex flex-col gap-0" data-testid="timeline">
      {entries.map((entry, i) => (
        <li key={entry.id} className="relative flex gap-3 pb-4 pl-1">
          <div className="flex flex-col items-center">
            <span
              aria-hidden
              className={clsx('mt-1 size-2.5 shrink-0 rounded-full ring-2 ring-[var(--h1-surface)]', {
                'bg-brand': entry.kind === 'handoff',
                'bg-danger': entry.kind === 'escalation',
                'bg-ok': entry.kind === 'stage',
                'bg-info': entry.kind === 'note' || entry.kind === 'task',
                'bg-line-strong': entry.kind === 'message' || entry.kind === 'score' || entry.kind === 'promise',
              })}
            />
            {i < entries.length - 1 && <span aria-hidden className="w-px flex-1 bg-line" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className={clsx('rounded px-1.5 py-0.5 text-[10px] font-medium', KIND_TONE[entry.kind])}>
                {entry.kind}
              </span>
              <span className="text-sm font-medium text-fg">{entry.title}</span>
              <time
                dateTime={entry.at}
                title={formatDateTime(entry.at)}
                className="ml-auto text-[11px] whitespace-nowrap text-fg-subtle"
              >
                {formatAgo(entry.at)}
              </time>
            </div>
            {entry.body && <p className="mt-0.5 text-sm break-words text-fg-muted">{entry.body}</p>}
            <p className="mt-0.5 text-[11px] text-fg-subtle">{entry.actor}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}
